<template>
  <div class="modal fade" ref="modal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-sm tc-dialog-top">
      <div class="modal-content tabler-confirm">
        <!-- top accent line -->
        <div class="tc-accent"></div>

        <div class="modal-body text-center">
          <!-- ⬇️ intent에 따라 색/아이콘 변경 -->
          <div class="tc-icon mx-auto mb-3" :class="`text-${intent}`">
            <!-- danger -->
            <svg v-if="intent==='danger'" xmlns="http://www.w3.org/2000/svg" width="44" height="44"
                 viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
                 stroke-linecap="round" stroke-linejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <!-- success -->
            <svg v-else-if="intent==='success'" xmlns="http://www.w3.org/2000/svg" width="44" height="44"
                 viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
                 stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 11l3 3L22 4"></path>
              <path d="M21 12a9 9 0 1 1-9-9"></path>
            </svg>
            <!-- info -->
            <svg v-else-if="intent==='info'" xmlns="http://www.w3.org/2000/svg" width="44" height="44"
                 viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
                 stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8"  x2="12.01" y2="8"></line>
            </svg>
            <!-- warning -->
            <svg v-else xmlns="http://www.w3.org/2000/svg" width="44" height="44"
                 viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
                 stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </div>

          <h5 class="fw-bold mb-2">{{ title }}</h5>
          <p class="text-secondary mb-0" v-html="message"></p>
        </div>

        <div class="modal-footer border-0 tc-footer">
          <button type="button" class="btn btn-outline-secondary" @click="cancel">
            {{ cancelText }}
          </button>
          <!-- ⬇️ intent에 맞는 버튼 색 -->
          <button type="button" class="btn ms-2" :class="confirmBtnClass" @click="ok">
            {{ confirmText }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from "bootstrap/dist/js/bootstrap.bundle.min.js";

export default {
  name: "TablerConfirm",
  data() {
    return {
      bs: null,
      resolver: null,
      resolved: false,
      title: "Are you sure?",
      message: "Do you really want to proceed?",
      confirmText: "Confirm",
      cancelText: "Cancel",
      // 기본 intent는 danger (삭제용)
      intent: "danger",  // 'danger' | 'success' | 'info' | 'warning'
      offsetTop: "32vh",
    };
  },
  computed: {
    accentColor() {
      // Tabler/Bootstrap 톤에 맞춰 매핑
      const map = {
        danger:  "#d63939",
        success: "#2563eb",
        info:    "#17c1e8",
        warning: "#f59f00",
      };
      return map[this.intent] || map.danger;
    },
    confirmBtnClass() {
      // Bootstrap 버튼 클래스
      return `btn-outline-${this.intent}`;
    },
  },
  mounted() {
    this.bs = new Modal(this.$refs.modal, { backdrop: true, keyboard: true, focus: true });
    this.$refs.modal.addEventListener("hidden.bs.modal", () => {
      if (this.resolver && !this.resolved) this.resolver(false);
      this.resolver = null;
      this.resolved = false;
    });
  },
  methods: {
    open(opts = {}) {
      this.title       = opts.title       ?? this.title;
      this.message     = opts.message     ?? this.message;
      this.confirmText = opts.confirmText ?? "Confirm";
      this.cancelText  = opts.cancelText  ?? "Cancel";
      // intent 우선, confirmColor로도 덮어쓰기 지원
      this.intent      = (opts.intent || opts.confirmColor || this.intent);

      this.offsetTop   = opts.offsetTop   ?? this.offsetTop;

      this.$nextTick(() => {
        const root = this.$refs.modal?.querySelector(".tabler-confirm");
        if (root) root.style.setProperty("--tc-accent", this.accentColor);

        const dlg = this.$refs.modal?.querySelector(".tc-dialog-top");
        if (dlg) dlg.style.setProperty("--tc-offset-top", this.offsetTop);
      });

      this.bs?.show();
      return new Promise((resolve) => {
        this.resolver = resolve;
        this.resolved = false;
      });
    },
    ok()     { if (this.resolver) { this.resolved = true; this.resolver(true); } this.bs?.hide(); },
    cancel() { if (this.resolver) { this.resolved = true; this.resolver(false);} this.bs?.hide(); },
  },
};
</script>

<style scoped>
.tabler-confirm {
  position: relative;
  border: 0;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(27,31,35,.08);
  overflow: hidden;
  --tc-accent: #d63939;
}

.tc-accent { height: 4px; width: 100%; background: var(--tc-accent); }

:deep(.tc-dialog-top) {
  --tc-offset-top: 8vh;
  max-width: 380px;
  margin: var(--tc-offset-top) auto 0;
}

.modal-body { padding: 24px 24px 8px; text-align: center; }
.tc-icon svg { width: 48px; height: 48px; }
h5 { font-size: 18px; margin: 8px 0 10px; }
.modal-body p { margin: 0 0 6px; line-height: 1.6; font-size: 14px; color: #6c757d; }

.tc-footer {
  display: flex; 
  justify-content: center; 
  gap: 4px;
  padding: 24px 10px 12px 10px;
}
.tc-footer .btn { min-height: 40px; min-width: 100px; padding: .5rem 1rem; border-radius: .5rem; }
</style>
